import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { AdminHeader } from "@/components/admin/AdminShared";
import { ROLE_LABELS } from "@/lib/helpers";
import { invalidateSettings } from "@/lib/settings";
import api, { API } from "@/lib/api";
import { ShieldCheck, User, Palette, Save, Eye, Type, PhoneCall } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SettingsAdmin() {
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings?draft=1").then((r) => setForm(r.data)); }, []);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("title", "Logo Situs");
      fd.append("category", "Logo");
      fd.append("file", file);
      const { data } = await api.post("/media-assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_url", data.storage_path);
      toast.success("Logo diunggah. Klik Publikasikan untuk menerapkan.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengunggah logo");
    }
  };

  const save = async (mode = "publish") => {
    setSaving(true);
    try {
      await api.put(`/settings${mode === "draft" ? "?mode=draft" : ""}`, form);
      if (mode === "publish") {
        invalidateSettings();
        toast.success("Perubahan dipublikasikan dan langsung tampil di situs.");
      } else {
        toast.success("Draf disimpan. Buka Pratinjau untuk melihat sebelum dipublikasikan.");
      }
    } catch {
      toast.error("Gagal menyimpan pengaturan");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="text-slate-400">Memuat pengaturan…</div>;

  return (
    <div>
      <AdminHeader title="Pengaturan" description="Edit konten & tampilan beranda serta identitas situs — perubahan langsung terlihat oleh publik."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => window.open("/?preview=1", "_blank")} data-testid="preview-site-btn"><Eye className="h-4 w-4 mr-1.5" /> Pratinjau Draf</Button>
            <Button variant="outline" onClick={() => save("draft")} disabled={saving} data-testid="save-draft-settings-btn"><Save className="h-4 w-4 mr-1.5" /> Simpan Draf</Button>
            <Button onClick={() => save("publish")} disabled={saving} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="save-settings-btn"><Save className="h-4 w-4 mr-1.5" /> {saving ? "Menyimpan…" : "Publikasikan"}</Button>
          </div>
        } />

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="settings-hero-form">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-4"><Type className="h-5 w-5 text-brandred" /> Tampilan Beranda (Hero)</h3>
          <div className="space-y-4">
            <div><Label>Teks Lencana Atas</Label><Input value={form.hero_badge || ""} onChange={(e) => set("hero_badge", e.target.value)} className="mt-1.5" data-testid="set-hero-badge" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Judul Utama</Label><Input value={form.hero_title || ""} onChange={(e) => set("hero_title", e.target.value)} className="mt-1.5" data-testid="set-hero-title" /></div>
              <div><Label>Judul Aksen (merah)</Label><Input value={form.hero_highlight || ""} onChange={(e) => set("hero_highlight", e.target.value)} className="mt-1.5" data-testid="set-hero-highlight" /></div>
            </div>
            <div><Label>Tagline</Label><Input value={form.hero_tagline || ""} onChange={(e) => set("hero_tagline", e.target.value)} className="mt-1.5" data-testid="set-hero-tagline" /></div>
            <div><Label>Deskripsi Hero</Label><Textarea value={form.hero_description || ""} onChange={(e) => set("hero_description", e.target.value)} rows={3} className="mt-1.5" data-testid="set-hero-description" /></div>
            <div><Label>Placeholder Kolom Pencarian</Label><Input value={form.search_placeholder || ""} onChange={(e) => set("search_placeholder", e.target.value)} className="mt-1.5" data-testid="set-search-placeholder" /></div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="settings-contact-form">
            <h3 className="font-bold text-navy flex items-center gap-2 mb-4"><PhoneCall className="h-5 w-5 text-brandred" /> Identitas & Kontak Situs</h3>
            <div className="space-y-4">
              <div><Label>Teks Bar Atas (top bar)</Label><Input value={form.topbar_text || ""} onChange={(e) => set("topbar_text", e.target.value)} className="mt-1.5" data-testid="set-topbar" /></div>
              <div><Label>Deskripsi Footer</Label><Textarea value={form.footer_description || ""} onChange={(e) => set("footer_description", e.target.value)} rows={2} className="mt-1.5" data-testid="set-footer-desc" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Alamat</Label><Input value={form.contact_address || ""} onChange={(e) => set("contact_address", e.target.value)} className="mt-1.5" data-testid="set-address" /></div>
                <div><Label>Telepon</Label><Input value={form.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} className="mt-1.5" data-testid="set-phone" /></div>
              </div>
              <div><Label>Email</Label><Input value={form.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} className="mt-1.5" data-testid="set-email" /></div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="settings-logo-form">
            <h3 className="font-bold text-navy flex items-center gap-2 mb-4"><Palette className="h-5 w-5 text-brandred" /> Logo &amp; Teks CTA Beranda</h3>
            <div className="space-y-4">
              <div>
                <Label>Logo Situs (navbar &amp; sidebar admin)</Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <img src={form.logo_url ? `${API}/files/${form.logo_url}` : "/assets/logo-bawaslu-emblem.png"} alt="Logo saat ini" className="h-11 w-auto bg-slate-100 rounded p-1 shrink-0" />
                  <Input type="file" accept=".png,.jpg,.jpeg" onChange={uploadLogo} data-testid="set-logo-file" className="flex-1" />
                </div>
                <p className="text-xs text-slate-400 mt-1">PNG/JPG, maks. 5MB. Klik Simpan Draf atau Publikasikan untuk menerapkan.</p>
              </div>
              <div><Label>Judul CTA</Label><Input value={form.cta_title || ""} placeholder="Punya aspirasi untuk Bawaslu?" onChange={(e) => set("cta_title", e.target.value)} className="mt-1.5" data-testid="set-cta-title" /></div>
              <div><Label>Deskripsi CTA</Label><Input value={form.cta_description || ""} placeholder="Sampaikan saran, kritik..." onChange={(e) => set("cta_description", e.target.value)} className="mt-1.5" data-testid="set-cta-desc" /></div>
              <div><Label>Teks Tombol CTA</Label><Input value={form.cta_button || ""} placeholder="Sampaikan Aspirasi" onChange={(e) => set("cta_button", e.target.value)} className="mt-1.5" data-testid="set-cta-button" /></div>
              <div><Label>Keterangan Diagram Satu Pintu</Label><Input value={form.diagram_caption || ""} placeholder="Konsep Satu Pintu..." onChange={(e) => set("diagram_caption", e.target.value)} className="mt-1.5" data-testid="set-diagram-caption" /></div>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-4"><User className="h-5 w-5 text-brandred" /> Profil Akun</h3>
          <div className="space-y-4">
            <div><Label>Nama</Label><Input defaultValue={user?.name} className="mt-1.5" disabled /></div>
            <div><Label>Email</Label><Input defaultValue={user?.email} className="mt-1.5" disabled /></div>
            <div><Label>Peran</Label><div className="mt-1.5 text-sm font-bold text-navy bg-surface rounded-lg px-3 py-2">{ROLE_LABELS[user?.role] || user?.role}</div></div>
          </div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h3 className="font-bold text-navy flex items-center gap-2 mb-4"><ShieldCheck className="h-5 w-5 text-brandred" /> Informasi Sistem</h3>
          <div className="space-y-2 text-sm text-slate-500">
            <div className="flex justify-between"><span>Aplikasi</span><span className="font-semibold text-navy">HARMONI BAWASLU</span></div>
            <div className="flex justify-between"><span>Versi Prototype</span><span className="font-semibold text-navy">1.0.0</span></div>
            <div className="flex justify-between"><span>Instansi</span><span className="font-semibold text-navy">Bawaslu Sultra</span></div>
            <div className="flex justify-between"><span>Autentikasi</span><span className="font-semibold text-navy">JWT + RBAC</span></div>
          </div>
        </div>
      </div>
    </div>
  );
}
